import React, { useEffect, useState, useMemo } from "react";
import { FaExclamationCircle, FaSync, FaSave, FaTimes } from "react-icons/fa";
import useAuth from "../../../../Hooks/useAuth";
import useDeviceSettingData from "../../../../Hooks/device/useDeviceSettingData";
import useUpdateDeviceSettingData from "../../../../Hooks/device/useUpdateDeviceSettingData";
import { v4 as uuidv4 } from "uuid";

// Import components
import SettingTypeSelector from "./SettingTypeSelector";
import GeneralSettings from "./GeneralSettings";
import PotSettings from "./PotSettings";
import ProfileSettings from "./ProfileSettings";

// Import utilities
import {
  getChangedSettings,
  getChangedPotSettings,
  getChangedSettingsForComponent,
  getChangedPotSettingsForComponent,
} from "./utils";

const LegacySettingsTab = ({ selectedDevice, setAlerts, isDrawer = false }) => {
  const { auth } = useAuth();
  const [activeType, setActiveType] = useState("GS");
  // Geçici ayarları tutmak için yeni state
  const [tempSettings, setTempSettings] = useState(null);
  // Değişiklik yapılıp yapılmadığını kontrol etmek için
  const [hasChanges, setHasChanges] = useState(false);
  const [activePotIndex, setActivePotIndex] = useState(null);

  const {
    getDeviceSetting,
    deviceSettings,
    deviceSettingIsLoading,
    deviceSettingError,
    setDeviceSettingData,
    settingsReadOnly,
  } = useDeviceSettingData();

  const {
    updateDeviceSetting,
    updateDeviceSettingIsLoading,
    updateDeviceSettingIsSuccess,
    updateDeviceSettingIsError,
    updateDeviceSettingError,
  } = useUpdateDeviceSettingData();

  // Göz indexlerini hesapla
  const allIndexes = useMemo(() => {
    const set = new Set();
    tempSettings?.pots?.forEach((pot) => {
      Object.values(pot).forEach((setting) => {
        if (setting && typeof setting.index === "number")
          set.add(setting.index);
      });
    });
    return Array.from(set).sort((a, b) => a - b);
  }, [tempSettings]);

  // Varsayılan olarak ilk göz seçili olsun
  useEffect(() => {
    if (activePotIndex === null && allIndexes.length > 0) {
      setActivePotIndex(allIndexes[0]);
    }
  }, [allIndexes, activePotIndex]);

  // deviceSettings değiştiğinde tempSettings'i güncelle
  useEffect(() => {
    if (deviceSettings) {
      setTempSettings(deviceSettings);
      setHasChanges(false);
    }
  }, [deviceSettings, activePotIndex]);

  // hasChanges state'ini güncelle
  useEffect(() => {
    if (deviceSettings && tempSettings) {
      let hasAnyChanges = false;

      // Genel ayarlar için kontrol
      if (activeType === "GS" || activeType === "PS") {
        const changedSettings = getChangedSettingsForComponent(
          deviceSettings,
          tempSettings
        );
        hasAnyChanges = Object.keys(changedSettings).length > 0;
      }

      // Göz ayarları için kontrol
      if (activeType === "CS" && tempSettings.pots) {
        tempSettings.pots.forEach((pot, potIndex) => {
          Object.keys(pot).forEach((key) => {
            if (pot[key] && typeof pot[key].index === "number") {
              const settingIndex = pot[key].index;
              const originalPot = deviceSettings.pots?.[potIndex];
              if (
                originalPot &&
                originalPot[key] &&
                originalPot[key].index === settingIndex
              ) {
                const originalValue = originalPot[key].value;
                const currentValue = pot[key].value;
                if (originalValue !== currentValue) {
                  hasAnyChanges = true;
                }
              }
            }
          });
        });
      }

      setHasChanges(hasAnyChanges);
    }
  }, [tempSettings, deviceSettings, activeType]);

  const changeCount = useMemo(() => {
    if (!deviceSettings || !tempSettings) return 0;
    if (activeType === "CS") {
      return Object.keys(
        getChangedPotSettingsForComponent(deviceSettings, tempSettings, activePotIndex)
      ).length;
    }
    return Object.keys(
      getChangedSettingsForComponent(deviceSettings, tempSettings)
    ).length;
  }, [deviceSettings, tempSettings, activeType, activePotIndex]);

  useEffect(() => {
    if (selectedDevice?._id) {
      getDeviceSetting({
        id: selectedDevice._id,
        token: auth.accessToken,
        type: activeType,
      });
    }
  }, [selectedDevice?._id, activeType]);

  useEffect(() => {
    if (updateDeviceSettingIsSuccess) {
      setHasChanges(false);
      setDeviceSettingData(tempSettings);
      const alertId = uuidv4();
      setAlerts((prev) => [
        ...prev,
        {
          id: alertId,
          severity: "success",
          message: "Ayarlar başarıyla güncellendi",
        },
      ]);
      // 5 saniye sonra alert'i kaldır
      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
      }, 5000);
    }
  }, [updateDeviceSettingIsSuccess]);

  useEffect(() => {
    if (updateDeviceSettingIsError) {
      const alertId = uuidv4();
      setAlerts((prev) => [
        ...prev,
        {
          id: alertId,
          severity: "error",
          message:
            updateDeviceSettingError?.data?.message ||
            "Ayarlar güncellenirken bir hata oluştu",
        },
      ]);

      // 5 saniye sonra alert'i kaldır
      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
      }, 5000);
    }
  }, [updateDeviceSettingIsError]);

  const handleSettingChange = (key, value) => {
    setTempSettings((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: value,
      },
    }));
  };

  const handlePotSettingChange = (index, key, value) => {
    setTempSettings((prev) => ({
      ...prev,
      pots: prev.pots.map((pot) => {
        if (
          pot[key] &&
          typeof pot[key].index === "number" &&
          pot[key].index === index
        ) {
          return {
            ...pot,
            [key]: { ...pot[key], value },
          };
        }
        return pot;
      }),
    }));
  };

  const handleSaveChanges = () => {
    // Değişen ayarları kontrol et
    let changedSettings = {};
    if (activeType === "CS") {
      changedSettings = getChangedPotSettings(
        deviceSettings,
        tempSettings,
        activePotIndex
      );
    } else {
      changedSettings = getChangedSettings(deviceSettings, tempSettings);
    }

    // Limit kontrolü
    const invalidSettings = Object.entries(changedSettings).filter(
      ([key, setting]) => {
        if (typeof setting.value === "number") {
          const value = setting.value;
          const lowerBound = setting.lowerBound;
          const upperBound = setting.upperBound || setting.upperBounde;
          return value < lowerBound || value > upperBound;
        } else if (setting.type === "SelectOptions") {
          return !setting.options.includes(setting.value);
        } else if (
          setting.type === "Input" &&
          typeof setting.value === "string" &&
          setting.regex
        ) {
          const regex = setting.regex;
          const value = setting.value;
          const regexTest = new RegExp(regex);
          return !regexTest.test(value);
        }
        return false;
      }
    );

    // Limit dışında değer varsa
    if (invalidSettings.length > 0) {
      const alertId = uuidv4();
      setAlerts((prev) => [
        ...prev,
        {
          id: alertId,
          severity: "error",
          message: "Bazı ayarlar limit dışında !",
        },
      ]);

      // 5 saniye sonra alert'i kaldır
      setTimeout(() => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== alertId));
      }, 5000);
      return;
    }

    // Yeni veri formatı oluştur
    let formattedData = {
      changes: {}, // Değişen değerler
      lastValue: {}, // Önceki değerler
      label: {}, // Etiketler
    };
    if (activeType === "CS") {
      formattedData.changes.index = activePotIndex;
      Object.entries(changedSettings).forEach(([key, setting]) => {
        deviceSettings.pots.forEach((pot) => {
          if (pot[key] && pot[key].index === activePotIndex) {
            formattedData.changes[key] = setting.value; // Yeni değer
            formattedData.lastValue[key] = pot[key].value; // Eski değer
            formattedData.label[key] = pot[key].label; // Label
          }
        });
      });
    } else {
      Object.entries(changedSettings).forEach(([key, setting]) => {
        formattedData.changes[key] = setting.value; // Yeni değer
        formattedData.lastValue[key] = deviceSettings[key].value; // Eski değer
        formattedData.label[key] = deviceSettings[key].label; // Label
      });
    }

    // Değişiklik varsa gönder
    if (Object.keys(formattedData.changes).length > 0) {
      updateDeviceSetting({
        id: selectedDevice._id,
        token: auth.accessToken,
        type: activeType,
        data: formattedData,
        key:
          activeType === "GS"
            ? "generalSetting"
            : activeType === "CS"
              ? "cupSetting"
              : "profileSetting",
      });
    }
  };

  const handleCancelChanges = () => {
    setTempSettings(deviceSettings);
    setHasChanges(false);
  };

  const renderContent = () => {
    if (!tempSettings) return null;

    switch (activeType) {
      case "GS":
        return (
          <GeneralSettings
            tempSettings={tempSettings}
            deviceSettings={deviceSettings}
            selectedDevice={selectedDevice}
            updateDeviceSettingIsLoading={updateDeviceSettingIsLoading}
            hasChanges={hasChanges}
            handleSettingChange={handleSettingChange}
            handleCancelChanges={handleCancelChanges}
            handleSaveChanges={handleSaveChanges}
            isDrawer={isDrawer}
            readOnly={settingsReadOnly}
          />
        );
      case "CS":
        return (
          <PotSettings
            tempSettings={tempSettings}
            deviceSettings={deviceSettings}
            selectedDevice={selectedDevice}
            updateDeviceSettingIsLoading={updateDeviceSettingIsLoading}
            hasChanges={hasChanges}
            allIndexes={allIndexes}
            activePotIndex={activePotIndex}
            setActivePotIndex={setActivePotIndex}
            handlePotSettingChange={handlePotSettingChange}
            handleCancelChanges={handleCancelChanges}
            handleSaveChanges={handleSaveChanges}
            isDrawer={isDrawer}
            readOnly={settingsReadOnly}
          />
        );
      case "PS":
        return (
          <ProfileSettings
            tempSettings={tempSettings}
            deviceSettings={deviceSettings}
            selectedDevice={selectedDevice}
            updateDeviceSettingIsLoading={updateDeviceSettingIsLoading}
            hasChanges={hasChanges}
            handleSettingChange={handleSettingChange}
            handleCancelChanges={handleCancelChanges}
            handleSaveChanges={handleSaveChanges}
            isDrawer={isDrawer}
            readOnly={settingsReadOnly}
          />
        );
      default:
        return null;
    }
  };

  const renderSkeleton = () => (
    <div
      className={`grid grid-cols-1 gap-4 pb-6 animate-pulse ${isDrawer ? "md:grid-cols-2" : "md:grid-cols-2 3xl:grid-cols-3"}`}
    >
      {[...Array(6)].map((_, idx) => (
        <div
          key={idx}
          className="overflow-hidden rounded-xl border border-gray-200 bg-white min-w-0"
        >
          {/* Kart başlığı — gerçek UI ile aynı */}
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/60">
            <div className="h-5 w-5 shrink-0 rounded-md bg-gray-200" />
            <div className="h-3 w-28 rounded bg-gray-200" />
          </div>
          {/* Kart içeriği — ayar kutuları grid */}
          <div className="p-3 pt-4 sm:p-4 sm:pt-5 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
              {[...Array(4)].map((__, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 min-w-0"
                >
                  <div className="h-3 w-20 rounded bg-gray-100" />
                  <div className="mt-1.5 h-8 rounded-lg bg-gray-100" />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex flex-1 min-h-0 flex-col relative">
      <div
        className={`flex-1 min-h-0 overflow-y-auto overflow-x-hidden -mt-2 sm:-m-4 sm:px-4 ${hasChanges ? "pb-24" : ""}`}
      >
        <SettingTypeSelector
          activeType={activeType}
          setActiveType={setActiveType}
          allIndexes={allIndexes}
          activePotIndex={activePotIndex}
          setActivePotIndex={setActivePotIndex}
          changedPotSettings={
            activeType === "CS" && deviceSettings && tempSettings
              ? getChangedPotSettingsForComponent(deviceSettings, tempSettings, activePotIndex)
              : {}
          }
        />

        {deviceSettingIsLoading ? (
          renderSkeleton()
        ) : deviceSettingError ? (
          <div className="flex items-center justify-center h-[400px] z-0">
            <div className="text-center px-4">
              <div className="text-red-500 mb-6">
                <FaExclamationCircle className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4" />
                <p className="text-lg md:text-xl mb-2">
                  Ayarlar yüklenirken bir hata oluştu
                </p>
                <p className="text-xs md:text-sm text-gray-500 mb-4">
                  Lütfen tekrar deneyin
                </p>
              </div>
              <button
                onClick={() =>
                  getDeviceSetting({
                    id: selectedDevice._id,
                    token: auth.accessToken,
                    type: activeType,
                  })
                }
                className="flex items-center gap-2 px-4 py-2 md:px-6 md:py-3 bg-indigo-600 text-white 
                rounded-lg hover:bg-indigo-700 transition-colors mx-auto text-sm md:text-base
                focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <FaSync
                  className={`w-3 h-3 md:w-4 md:h-4 ${deviceSettingIsLoading ? "animate-spin" : ""
                    }`}
                />
                {deviceSettingIsLoading ? "Yükleniyor..." : "Yeniden Dene"}
              </button>
            </div>
          </div>
        ) : (
          renderContent()
        )}
      </div>

      {/* Salt okunur uyarısı (cihaz bağlı değilken veritabanından gösterim) */}
      {settingsReadOnly && (
        <div className="relative z-10 shrink-0 border-t border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm text-amber-800">
            Cihaz bağlı değil; veritabanındaki son ayarlar gösteriliyor. Değişiklik yapılamaz.
          </p>
        </div>
      )}

      {/* Drawer/sayfa altında sabit: X ayar değişti + Kaydet / İptal — salt okunur değilse */}
      {hasChanges && !settingsReadOnly && (
        <div className="relative z-10 shrink-0 border-t border-gray-200 bg-white px-4 pt-3 pb-4 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <p className="text-sm text-gray-600 min-w-0 flex-1 sm:flex-initial">
              <span className="font-semibold text-slate-700">{changeCount}</span> ayar değişti. Kaydetmeden çıkarsanız kaybolacak.
            </p>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                onClick={handleCancelChanges}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition hover:border-gray-400"
                disabled={updateDeviceSettingIsLoading}
              >
                <FaTimes className="h-4 w-4" />
                İptal
              </button>
              <button
                type="button"
                onClick={handleSaveChanges}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:shadow-lg disabled:opacity-70"
                disabled={updateDeviceSettingIsLoading}
              >
                {updateDeviceSettingIsLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                ) : (
                  <FaSave className="h-4 w-4" />
                )}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LegacySettingsTab;
